import React from 'react';
import { Map } from 'mapbox-gl';
import { MapOptionsAction, MapOptions } from 'page-explore';
import { useQueryState } from 'router';
import MapRoutes from './routes';
import MapRidesheds from './ridesheds';
import MapHexBins from './hex-bins';
import useDocumentTitle from 'common/use-document-title';
import { defineMessages, useIntl } from 'react-intl';
import CurbEvents from './curb-events';
import { useMapView } from 'common/use-map-view';
import { FeatureFlaggedFeature } from 'graphql.g';
import { useUser } from 'common/user';
import MapAreaOriginDestinationReports from './area-origin-destination-reports';

type Props = {
    map: Map | null;
    mapOptions: MapOptions;
    dispatchMapOptions: React.Dispatch<MapOptionsAction>;
    clientHeight: number | null;
};

export function useMapModes(): MapMode[] {
    const user = useUser();
    const { enabledFeatures: mapviewEnabledFeatures } = useMapView();

    const baseModes: MapMode[] = [
        'trip_starts',
        'trip_ends',
        'routes',
        'curb_events'
    ];
    if (
        mapviewEnabledFeatures.includes(FeatureFlaggedFeature.area_ridesheds) ||
        user.isStaff
    ) {
        baseModes.push('ridesheds');
    }
    if (
        mapviewEnabledFeatures.includes(
            FeatureFlaggedFeature.area_origin_destination_counts
        ) ||
        user.isStaff
    ) {
        baseModes.push('area_origin_destination_reports');
    }
    return baseModes;
}

export type MapMode =
    | 'trip_starts'
    | 'trip_ends'
    | 'routes'
    | 'curb_events'
    | 'ridesheds'
    | 'area_origin_destination_reports';

export function useMapMode(active: boolean = true) {
    const mapModes = useMapModes();
    const [mode, setMode] = useQueryState<MapMode>('mode', {
        constrain: {
            options: new Set(mapModes),
            fallbackValue: 'routes'
        },
        active,
        addToHistory: true
    });
    return [mode, setMode] as const;
}

const messages = defineMessages({
    'routes-document-title': 'Routes · Ride Report',
    'trip_starts-document-title': 'Trip Starts · Ride Report',
    'trip_ends-document-title': 'Trip Ends · Ride Report',
    'curb_events-document-title': 'Curb Events · Ride Report',
    'ridesheds-document-title': 'Ridesheds · Ride Report',
    'area_origin_destination_reports-document-title':
        'Area Origin/Destination Counts · Ride Report'
});

function HeatmapsPage(props: Props) {
    const [mode] = useMapMode();
    const intl = useIntl();
    useDocumentTitle(intl.formatMessage(messages[`${mode}-document-title`]));

    if (mode === 'routes') return <MapRoutes {...props} />;
    if (mode === 'trip_ends') return <MapHexBins {...props} metric="ends" />;
    if (mode === 'trip_starts')
        return <MapHexBins {...props} metric="starts" />;
    if (mode === 'curb_events') return <CurbEvents {...props} />;
    if (mode === 'ridesheds') return <MapRidesheds {...props} />;
    if (mode === 'area_origin_destination_reports')
        return <MapAreaOriginDestinationReports {...props} />;

    throw new Error(`Unknown mode: ${mode}`);
}

export default HeatmapsPage;
